import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Cover from '../Cover/Cover';
import Lightbox from '../Lightbox/Lightbox';
import Tabs from '../Tabs/Tabs';
import SaveState from '../../constants/SaveState';

/**
 * `TabbedLightbox` is a lightbox that should be used for more advanced tasks like configuring assets or categories. The
 * lightbox has a {@link Sidebar} to the left and {@link Tabs} to the right.
 *
 * The lightbox may also be used for non-tabbed content provided that you only pass a single child component and the child
 * does not have a *name* prop set.
 *
 * The sidebar should be used to display a summary of the item that the lightbox represents so that the user does not have to
 * click on all tabs to get an overview. The sidebar also shows the title of the item and cover art - or a custom component
 * such as a video player if the item represents a playable resource. Optional tag and state icons can be displayed over the
 * sidebar cover.
 *
 * The lightbox will automatically create the tabs for you provided that each child is passed a `name` property:
 * ````
 * <TabbedLightbox>
 *   <div name="Tab 1">...</div>
 *   <div name="Tab 2">...</div>
 * </TabbedLightbox>
 * ````
 * `TabbedLightbox` is responsive and will adopt to all screen resolutions. Tab contents you add to this dialog should also
 * be responsive.
 *
 * If you need simpler lightbox, you should use the {@link Lightbox} instead.
 *
 * @property {node} badges One or more badges that will be added to the top left corner of the cover image, to the right of
 * *tag* if that prop is defined. Defaults to no badges.
 * @property {array} badgeNames An array of icon names supported by the {@link Icon} component. Defaults to no badges.
 * @property {node} children The child components that will make up the tabs in your lightbox. The lightbox will
 * automatically create the tabs for you provided that each child is passed a *name* property. You may use the lightbox for
 * non tabbed content by passing a single child with no *name* prop set.
 * @property {string} className The class name of this lightbox. Will be appended to "core-tabbed-lightbox".
 * @property {boolean} closeOnClickOutside If set to `true`, the *onClose* callback will be called when the user clicks
 * outside the lightbox boundaries. Defaults to `true`.
 * @property {string|node} cover The URI of the cover image you want to show in the sidebar, or a custom component that
 * should act as a cover (this could for instance be a player instead).
 * @property {array} fields An array of objects of type `{ name, value }` properties that will be displayed in the sidebar.
 * The fields will be read-only.
 * @property {string|number} id A field directly under the title that shows the ID of the item shown in the lightbox. The
 * field is not shown by default.
 * @property {number} level Determines the heading level of the title component. Defaults to 2 (`h2`).
 * @property {function} onChange A callback that is called whenever the user clicks on a tab. The callback is passed the
 * same arguments as the *onChange* callback in {@link Tabs}.
 * @property {function} onClose A callback that is called when the lightbox is closed.
 * @property {enum} saveState If set to either of the constants `SaveState.NONE`, `SaveState.SAVING`, `SaveState.SAVED` or
 * `SaveState.ERROR`, the corresponding state will be shown in the upper right corner of the lightbox. No state will be shown
 * by default.
 * @property {number} selected The selected tab index. Defaults to 0.
 * @property {string} tag A tag that will be displayed over the cover image (or custom component) in the sidebar. Defaults to
 * no tag.
 * @property {string} title The sidebar title. A `h2` will be used by default, but you may change the heading level by
 * setting the *level* prop. Title defaults to "Untitled".
 *
 * @see Cover, Lightbox, Tabs
 */

 TabbedLightbox.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  closeOnClickOutside: PropTypes.bool, // eslint-disable-line
  cover: PropTypes.oneOfType([PropTypes.string, PropTypes.node]), // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/no-unused-prop-types
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // eslint-disable-line react/no-unused-prop-types
  level: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
  onChange: PropTypes.func,
  onClose: PropTypes.func,
  saveState: PropTypes.oneOf([
    SaveState.SAVING,
    SaveState.SAVED,
    SaveState.NONE,
  ]),
  selected: PropTypes.number,
  tag: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  title: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
};

TabbedLightbox.defaultProps = {
  closeOnClickOutside: true,
  selected: 0,
};

export default function TabbedLightbox (props) {
  const {
    children,
    className,
    closeOnClickOutside,
    onClose,
    saveState,
    selected,
    onChange,
    ...rest
  } = props;
  const [selectedIndex, setSelectedIndex] = useState(selected);
  let content = children;

  useEffect(() => {
    setSelectedIndex(selected);
  }, [selected]);

  const onChangeTabs = (e, index) => {
    if (onChange) {
      onChange(e, index);
    } 
    setSelectedIndex(index);
  };

  if (Array.isArray(children) || children.props.name) {
    content = (
      <Tabs
        className='core-tabbed-lightbox-tabs'
        onChange={onChangeTabs}
        selected={selectedIndex || selected}
      >
        {children}
      </Tabs>
    );
  }

  return (
    <Lightbox
      className={cx('core-tabbed-lightbox', className, {
        'no-tabs': content === children,
      })}
      showHeader={false}
      onClose={onClose}
      saveState={saveState}
      closeOnClickOutside={closeOnClickOutside}
    >
      <div className='core-tabbed-lightbox-content'>
        <Cover {...rest} />
        {content}
      </div>
    </Lightbox>
  );
};
